import * as THREE from 'three/webgpu';
import {ReferencePlane} from '../../reference/ReferencePlane';
import {SketchGroup} from '../../mesh/sketch/SketchGroup';
import {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import {SketchLineObject} from '../../mesh/sketch/SketchLineObject';
import {SketchCircleObject} from '../../mesh/sketch/SketchCircleObject';
import {SketchArcObject} from '../../mesh/sketch/SketchArcObject';
import {SketchEllipseObject} from '../../mesh/sketch/SketchEllipseObject';
import {SketchParabolaObject} from '../../mesh/sketch/SketchParabolaObject';
import {SketchSplineObject} from '../../mesh/sketch/SketchSplineObject';
import {SketchStyleSplineObject} from '../../mesh/sketch/SketchStyleSplineObject';
import {SketchSlotObject, type SlotJunctionPoints} from '../../mesh/sketch/SketchSlotObject';
import {
    SketchCollectionCreationSource,
    SketchCollectionObject,
} from '../../mesh/sketch/SketchCollectionObject';
import {
    EllipseControlPointRole,
    SketchPointBoxSelectionRole,
    SketchPointKind,
    type CollectSource,
    type ISketchObject,
} from '../../mesh/sketch/ISketchObject';
import type {SketchSlotCommand} from '../../draw/SketchSlotDrawingHandler';
import type {SketchBezierCurveMetadata} from '../../mesh/sketch/SketchSplineObject';
import {PointsMaterialFactory} from '../../material/PointsMaterialFactory';
import type {SketchRelationDefinition} from '../solver/constraint/relationDefinition/SketchRelationDefinition';
import {SketchEntitySymmetricConstraint}
    from '../solver/constraint/relationDefinition/SketchEntitySymmetricConstraint';
import {SketchRelationKind} from '../solver/constraint/relation-compatibility/SketchRelationKinds';
import {isNumericSketchRelationKind} from '../solver/constraint/relation-compatibility/SketchRelationDefinitions';
import {
    resolveArcRelationConstraintClassMap,
    resolveCircleRelationConstraintClassMap,
    resolveLineRelationConstraintClassMap,
    resolvePointRelationConstraintClassMap,
} from '../solver/constraint/GeometricRelationConstraints';
import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isMirrorAxisGeometryProvider,
    isSketchParabolaObject,
    isSketchPointObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../../mesh/guards';
import {remapSketchPersistentPlainDataIds} from './remapSketchPersistentPlainDataIds';
import {createSketchEntityPersistentId} from '../identity/SketchPersistentIdFactory';
import {SketchCircularPatternObject} from '../circular-pattern/SketchCircularPatternObject';
import {SketchMirrorAxisReferenceProvider} from '../entities-transform/SketchMirrorAxisReferenceProvider';
import type {SketchGroupPersistenceDeserializerOptions} from '../../../types/sketch/mirror/SketchMirrorAxisReferenceTypes';
import type {
    SketchPersistentCollectionRecord,
    SketchPersistentDocument,
    SketchPersistentEntityRecord,
    SketchPersistentPlainData,
    SketchPersistentRelationRecord,
    SketchPersistentSlotRecord,
    SketchPersistentVector3,
} from './SketchPersistenceTypes';
import {createSketchPatternedConstraintFromFormulaPayload} from '../solver/constraint/relationDefinition/pattern/SketchPatternedConstraint';

/**
 * 将持久化三元组转换为 THREE.Vector3。
 * @param tuple 持久化三元组。
 * @returns 新建向量。
 */
function toVector3(tuple: SketchPersistentVector3): THREE.Vector3 {
    return new THREE.Vector3(tuple[0], tuple[1], tuple[2]);
}

/**
 * 草图组持久化反序列化器。
 *
 * 职责：
 * 1. 把持久化文档重建为可编辑的运行时 SketchGroup，覆盖全部实体类型并保持持久身份不变；
 * 2. 三阶段重建：点池 → 全部边实体 → 容器（槽口/集合），容器构造函数接管成员父子关系；
 * 3. 统一挂载：容器与未被容器接管的边/点挂到草图组，与运行时绘制结构一致；
 * 4. 几何关系按“关系类型 + 首目标类型”经既有约束类映射重建，数值关系恢复锁定值。
 *
 * 边界：
 * 1. 输入必须是 `parseSketchPersistentDocument` 校验过的文档；
 * 2. 重建过程不触发任何约束求解（文档记录的就是已求解的最终位形）；
 * 3. 点材质使用工厂规范材质，显示态由上层管理器统一应用。
 */
export class SketchGroupPersistenceDeserializer {
    /** 外部镜像轴运行时恢复依赖。 */
    private readonly options: SketchGroupPersistenceDeserializerOptions;

    /**
     * @param options 可选运行时 resolver；缺省时稳定引用以悬空状态恢复。
     */
    public constructor(options: SketchGroupPersistenceDeserializerOptions = {}) {
        this.options = options;
    }

    /**
     * 反序列化草图持久化文档。
     * @param document 已校验的持久化文档。
     * @returns 重建完成的草图组（尚未挂入场景）。
     */
    public deserialize(document: SketchPersistentDocument): SketchGroup {
        const referencePlane = this.rebuildReferencePlane(document);
        const group = new SketchGroup({referencePlane, name: document.name});
        group.persistentId = document.sketchId;

        // 阶段 1：点池（先不挂载，父子关系由边/容器构造函数决定）。
        const pointInstanceMap = new Map<string, SketchPointObject>();
        document.points.forEach((record) => {
            const pointKind = record.pointKind as SketchPointKind;
            /**
             * 持久化文档不保存瞬时交互材质，恢复时必须按点类型回到规范默认态。
             * CameraSpriteNodeMaterial 是 TSL 节点材质，直接 clone 会把
             * sizeAttenuation 等节点配置退回 Three.js 默认值，导致挂入场景后的
             * 第一帧按世界尺寸渲染；因此这里直接复用材质工厂维护的规范实例。
             */
            const material = pointKind === SketchPointKind.ControlPoint
                ? PointsMaterialFactory.getSketchControlPointSpriteMaterial()
                : PointsMaterialFactory.getSketchPointDefaultSpriteMaterial();
            const point = new SketchPointObject({
                position: toVector3(record.position),
                pointKind,
                material,
                referencePlane,
            });
            point.persistentId = record.pointId;
            if (record.boxSelectionRole) {
                point.boxSelectionRole = record.boxSelectionRole as SketchPointBoxSelectionRole;
            }
            if (record.isAuxiliaryObject) {
                point.isAuxiliaryObject = true;
            }
            pointInstanceMap.set(record.pointId, point);
        });
        const resolvePoolPoint = (pointId: string): SketchPointObject => {
            const point = pointInstanceMap.get(pointId);
            if (!point) {
                throw new Error(`草图持久化重建失败：点池缺少 ${pointId}。`);
            }
            return point;
        };
        /**
         * 查询可选的池化点，供圆系实体区分共享圆心引用与旧版私有圆心声明。
         * @param pointId 待查询的持久 ID。
         * @returns 命中的池化点；未命中时返回 undefined。
         */
        const findPoolPoint = (pointId: string): SketchPointObject | undefined => (
            pointInstanceMap.get(pointId)
        );

        // 阶段 2：全部边实体（含容器成员），暂不挂载。
        const edgeInstanceMap = new Map<string, ISketchObject>();
        const containerRecords: (SketchPersistentSlotRecord | SketchPersistentCollectionRecord)[] = [];
        document.entities.forEach((record) => {
            if (record.kind === 'slot' || record.kind === 'collection') {
                containerRecords.push(record);
                return;
            }
            edgeInstanceMap.set(
                record.entityId,
                this.rebuildEdge(record, referencePlane, resolvePoolPoint, findPoolPoint),
            );
        });
        const resolveEdge = (entityId: string): ISketchObject => {
            const edge = edgeInstanceMap.get(entityId);
            if (!edge) {
                throw new Error(`草图持久化重建失败：实体缺少 ${entityId}。`);
            }
            return edge;
        };

        // geometryData 反向归一化映射：持久 ID → 重建后运行时 uuid。
        const persistentIdToUuid = new Map<string, string>();
        pointInstanceMap.forEach((point, pointId) => {
            persistentIdToUuid.set(pointId, point.uuid);
        });
        edgeInstanceMap.forEach((edge, entityId) => {
            persistentIdToUuid.set(entityId, edge.uuid);
        });

        // 阶段 3：容器（构造函数接管成员父子关系与拓扑绑定）。
        const containerInstances: THREE.Object3D[] = [];
        containerRecords.forEach((record) => {
            const container = record.kind === 'slot'
                ? this.rebuildSlot(record, referencePlane, resolvePoolPoint, resolveEdge, persistentIdToUuid)
                : this.rebuildCollection(record, referencePlane, resolvePoolPoint, resolveEdge, persistentIdToUuid);
            containerInstances.push(container);
        });

        // 统一挂载：容器 → 未被容器接管的边 → 未被任何对象接管的点。
        containerInstances.forEach((container) => {
            group.add(container);
        });
        edgeInstanceMap.forEach((edge) => {
            const edgeObject = edge as unknown as THREE.Object3D;
            if (!edgeObject.parent) {
                group.add(edgeObject);
            }
        });
        pointInstanceMap.forEach((point) => {
            if (!point.parent) {
                group.add(point);
            }
        });

        // 圆周草图阵列定义依赖实体持久 ID，必须在全部实体完成挂载后恢复。
        document.circularPatterns?.forEach((record) => {
            const referencedEntityIds = new Set([
                ...record.sourceEntityPersistentIds,
                ...record.instances.flatMap((instance) => instance.entityPersistentIds),
            ]);
            referencedEntityIds.forEach((persistentId) => {
                if (!group.getObjectByPersistentId(persistentId)) {
                    throw new Error(`草图持久化重建失败：圆周阵列引用的实体不存在（${persistentId}）。`);
                }
            });
            const pattern = new SketchCircularPatternObject({
                sourceEntityPersistentIds: record.sourceEntityPersistentIds,
                parameters: {
                    center2d: [record.center2d[0], record.center2d[1]],
                    radius: record.radius,
                    startAngleDeg: THREE.MathUtils.radToDeg(record.startAngleRad),
                    totalAngleDeg: THREE.MathUtils.radToDeg(record.totalAngleRad),
                    equalSpacing: record.equalSpacing,
                    spacingAngleDeg: THREE.MathUtils.radToDeg(record.spacingAngleRad),
                    reverseDirection: record.reverseDirection,
                    instanceCount: record.instanceCount,
                    rotateInstances: record.rotateInstances,
                    showRadiusDimension: record.showRadiusDimension,
                    showSpacingDimension: record.showSpacingDimension,
                    showInstanceCount: record.showInstanceCount,
                    skippedInstanceIndexes: [...record.skippedInstanceIndexes],
                },
                instances: record.instances,
            });
            pattern.persistentId = record.persistentId;
            const owningCollection = group.children.find((child) => (
                child instanceof SketchCollectionObject
                && child.geometryData?.circularPatternPersistentId === record.persistentId
            ));
            if (owningCollection instanceof SketchCollectionObject) {
                owningCollection.add(pattern);
            } else {
                // 通用圆周草图阵列不属于多边形聚合，仍挂载在草图组根层。
                group.add(pattern);
            }
        });

        // 外部镜像轴是正式关系目标，但不创建隐藏草图几何；必须在关系重建前进入组内身份索引。
        document.mirrorAxisReferences?.forEach((record) => {
            const provider = new SketchMirrorAxisReferenceProvider({
                reference: record.reference,
                referencePlane,
                resolver: this.options.mirrorAxisReferenceResolver ?? null,
            });
            provider.persistentId = record.targetId;
            group.addSketchReferenceTarget(provider);
        });

        this.rebuildRelations(document, group);
        return group;
    }

    /**
     * 重建参考平面。
     * @param document 持久化文档。
     * @returns 参考平面实例。
     */
    private rebuildReferencePlane(document: SketchPersistentDocument): ReferencePlane {
        const record = document.referencePlane;
        return new ReferencePlane(
            record.displayName,
            toVector3(record.normal),
            record.constant,
            {
                xAxis: toVector3(record.xAxis),
                yAxis: toVector3(record.yAxis),
            },
            record.hostFace ? {objectId: record.hostFace.objectId, faceIndex: record.hostFace.faceIndex} : null,
            record.referencePlaneId,
        );
    }

    /**
     * 重建单条边实体。
     * @param record 实体记录（非容器）。
     * @param referencePlane 参考平面。
     * @param resolvePoolPoint 点池解析函数。
     * @param findPoolPoint 可选点池查询函数。
     * @returns 重建后的边对象。
     */
    private rebuildEdge(
        record: SketchPersistentEntityRecord,
        referencePlane: ReferencePlane,
        resolvePoolPoint: (pointId: string) => SketchPointObject,
        findPoolPoint: (pointId: string) => SketchPointObject | undefined,
    ): ISketchObject {
        if (record.kind === 'line') {
            const line = new SketchLineObject({
                referencePlane,
                startPoint: resolvePoolPoint(record.startPointId),
                endPoint: resolvePoolPoint(record.endPointId),
                isInfiniteLength: record.isInfiniteLength,
                //------ 中心线语义 开始------
                ...(record.isCenterline === true ? {isCenterline: true} : {}),
                //------ 中心线语义 结束------
                ...(record.isAuxiliaryObject === true ? {isAuxiliaryObject: true} : {}),
            });
            line.persistentId = record.entityId;
            if (record.isConstruction) {
                line.isConstruction = true;
            }
            return line;
        }

        if (record.kind === 'circle') {
            const pooledCenterPoint = findPoolPoint(record.centerPointId);
            const circle = new SketchCircleObject({
                center: toVector3(record.centerWorld),
                radius: record.radius,
                referencePlane,
                isConstruction: record.isConstruction,
                ...(pooledCenterPoint ? {centerPoint: pooledCenterPoint} : {}),
            });
            circle.persistentId = record.entityId;
            if (!pooledCenterPoint) {
                circle.getCenterControlPoint().persistentId = record.centerPointId;
            }
            return circle;
        }

        if (record.kind === 'arc') {
            const pooledCenterPoint = findPoolPoint(record.centerPointId);
            const arc = new SketchArcObject({
                referencePlane,
                definition: {
                    centerWorld: toVector3(record.centerWorld),
                    radius: record.radius,
                    startAngle: record.startAngle,
                    endAngle: record.endAngle,
                    clockwise: record.clockwise,
                    segments: record.segments,
                },
                startPoint: resolvePoolPoint(record.startPointId),
                endPoint: resolvePoolPoint(record.endPointId),
                ...(pooledCenterPoint ? {
                    centerPoint: pooledCenterPoint,
                    centerPointOwnership: 'external' as const,
                } : {}),
            });
            arc.persistentId = record.entityId;
            if (!pooledCenterPoint) {
                arc.getCenterPoint().persistentId = record.centerPointId;
            }
            if (record.isConstruction) {
                arc.isConstruction = true;
            }
            return arc;
        }

        if (record.kind === 'ellipse') {
            const ellipse = new SketchEllipseObject({
                referencePlane,
                definition: {
                    centerWorld: toVector3(record.centerWorld),
                    radiusX: record.radiusX,
                    radiusY: record.radiusY,
                    rotation: record.rotation,
                    startAngle: record.startAngle,
                    endAngle: record.endAngle,
                    clockwise: record.clockwise,
                    segments: record.segments,
                },
                startPoint: resolvePoolPoint(record.startPointId),
                endPoint: resolvePoolPoint(record.endPointId),
                centerPoint: resolvePoolPoint(record.centerPointId),
            });
            ellipse.persistentId = record.entityId;
            record.cardinalControlPoints.forEach((cardinal) => {
                ellipse.registerCardinalControlPoint(
                    resolvePoolPoint(cardinal.pointId),
                    cardinal.role as EllipseControlPointRole,
                );
            });
            if (record.isConstruction) {
                ellipse.isConstruction = true;
            }
            if (record.hasContour) {
                ellipse.syncContour();
            }
            return ellipse;
        }

        if (record.kind === 'parabola') {
            const parabola = new SketchParabolaObject({
                referencePlane,
                definition: {
                    focalWorld: toVector3(record.focalWorld),
                    apexWorld: toVector3(record.apexWorld),
                    startParameter: record.startParameter,
                    endParameter: record.endParameter,
                    segments: record.segments,
                },
                focalPoint: resolvePoolPoint(record.focalPointId),
                apexPoint: resolvePoolPoint(record.apexPointId),
                startPoint: resolvePoolPoint(record.startPointId),
                endPoint: resolvePoolPoint(record.endPointId),
            });
            parabola.persistentId = record.entityId;
            if (record.isConstruction) {
                parabola.isConstruction = true;
            }
            return parabola;
        }

        if (record.kind === 'spline') {
            const controlPoints = record.controlPointIds.map((pointId) => resolvePoolPoint(pointId));
            const startPoint = controlPoints[0]!;
            const endPoint = controlPoints[controlPoints.length - 1]!;
            const options = {
                referencePlane,
                startPoint,
                endPoint,
                controlPoints: controlPoints.slice(1, -1),
                curvePoints: record.curvePoints.map((point) => toVector3(point)),
                curveMetadata: record.curveMetadata as unknown as Partial<SketchBezierCurveMetadata>,
                curveEvaluationDefinition: record.curveEvaluationDefinition,
            };
            const spline = record.variant === 'style'
                ? new SketchStyleSplineObject(options)
                : new SketchSplineObject(options);
            spline.persistentId = record.entityId;
            if (record.isConstruction) {
                spline.isConstruction = true;
            }
            return spline;
        }

        throw new Error(`草图持久化重建失败：未知实体类型 ${String((record as {kind: string}).kind)}。`);
    }

    /**
     * 重建槽口容器。
     * @param record 槽口记录。
     * @param referencePlane 参考平面。
     * @param resolvePoolPoint 点池解析函数。
     * @param resolveEdge 实体解析函数。
     * @param persistentIdToUuid
     * @returns 重建后的槽口对象。
     */
    private rebuildSlot(
        record: SketchPersistentSlotRecord,
        referencePlane: ReferencePlane,
        resolvePoolPoint: (pointId: string) => SketchPointObject,
        resolveEdge: (entityId: string) => ISketchObject,
        persistentIdToUuid: ReadonlyMap<string, string>,
    ): SketchSlotObject {
        const junctionPoints: SlotJunctionPoints = {
            p1: resolvePoolPoint(record.junctionPointIds.p1),
            p2: resolvePoolPoint(record.junctionPointIds.p2),
            p3: resolvePoolPoint(record.junctionPointIds.p3),
            p4: resolvePoolPoint(record.junctionPointIds.p4),
        };
        const resolveArc = (entityId: string): SketchArcObject => {
            const edge = resolveEdge(entityId);
            if (!isSketchArcObject(edge)) {
                throw new Error(`草图持久化重建失败：槽口成员 ${entityId} 不是圆弧。`);
            }
            return edge;
        };
        const resolveLine = (entityId: string): SketchLineObject => {
            const edge = resolveEdge(entityId);
            if (!isSketchLineObject(edge)) {
                throw new Error(`草图持久化重建失败：槽口成员 ${entityId} 不是直线。`);
            }
            return edge;
        };

        let slot: SketchSlotObject;
        if (record.slotKind === 'arc') {
            /**
             * 新文档直接按 ID 解析构造中心线弧；旧文档缺省该字段时按几何数据合成，
             * 保证旧版本保存的圆弧槽口重载后仍具备实体级中心线显示（复用既有共享点，不新建点对象）。
             */
            const constructionCenterlineArc = record.constructionCenterlineArcId
                ? resolveArc(record.constructionCenterlineArcId)
                : this.synthesizeArcSlotConstructionCenterlineArc(
                    record,
                    referencePlane,
                    resolvePoolPoint,
                    resolveArc(record.startCapArcId),
                    resolveArc(record.endCapArcId),
                );
            slot = new SketchSlotObject({
                mode: record.mode as SketchSlotCommand,
                referencePlane,
                outerArc: resolveArc(record.outerArcId!),
                innerArc: resolveArc(record.innerArcId!),
                startCapArc: resolveArc(record.startCapArcId),
                endCapArc: resolveArc(record.endCapArcId),
                junctionPoints,
                arcCenterPoint: resolvePoolPoint(record.arcCenterPointId!),
                centerArcMidpoint: resolvePoolPoint(record.centerArcMidpointId!),
                constructionCenterlineArc,
            });
        } else {
            slot = new SketchSlotObject({
                mode: record.mode as SketchSlotCommand,
                referencePlane,
                topLine: resolveLine(record.topLineId!),
                bottomLine: resolveLine(record.bottomLineId!),
                startCapArc: resolveArc(record.startCapArcId),
                endCapArc: resolveArc(record.endCapArcId),
                junctionPoints,
                centerlineStartPoint: resolvePoolPoint(record.centerlineStartPointId!),
                centerlineEndPoint: resolvePoolPoint(record.centerlineEndPointId!),
                centerPoint: resolvePoolPoint(record.centerPointId!),
                constructionCenterline: resolveLine(record.constructionCenterlineId!),
            });
        }

        slot.persistentId = record.entityId;
        // 复刻 cloneSelf 的收口：边重新绑定宿主并重建 collectSet。
        slot.getEdges().forEach((edge) => {
            edge.attachClosedCollection(slot);
        });
        // 构造中心线（直槽口直线 / 圆弧槽口弧线）同属槽口闭合宿主，与绘制装配路径保持一致。
        slot.constructionCenterline?.attachClosedCollection(slot);
        slot.constructionCenterlineArc?.attachClosedCollection(slot);
        if (record.geometryData !== null) {
            slot.geometryData = remapSketchPersistentPlainDataIds(
                record.geometryData,
                persistentIdToUuid,
            ) as Record<string, unknown>;
        }
        slot.collectSet = new Set(slot.getEdges() as unknown as CollectSource[]);
        if (record.isConstruction) {
            slot.isConstruction = true;
        }
        if (record.hasContour) {
            slot.syncContour();
        }
        return slot;
    }

    /**
     * 为旧版圆弧槽口文档合成构造中心线弧。
     *
     * 设计说明：
     * 1. 旧文档槽口记录没有 `constructionCenterlineArcId`，但几何元数据保留了
     *    中心线弧的圆心/半径/起终角/方向，据此重建与运行时绘制装配同义的实体；
     * 2. **必须严格复用既有共享点对象**——起点/终点取端帽弧圆心点，圆心取槽口弧圆心控制点，
     *    新建点会造成“坐标相同但身份不同”的重复点，破坏槽口共享点拓扑不变量
     *    （拾取/约束/持久化都会因此出错）；
     * 3. 合成实体分配全新持久 ID：它不是旧文档既有成员，不能复用任何文档内 ID。
     *
     * @param record 槽口记录。
     * @param referencePlane 参考平面。
     * @param resolvePoolPoint 点池解析函数。
     * @param startCapArc 已重建的起始端帽弧。
     * @param endCapArc 已重建的终止端帽弧。
     * @returns 合成的构造中心线弧。
     * @throws 当几何元数据不足以定义中心线弧时抛出错误。
     */
    private synthesizeArcSlotConstructionCenterlineArc(
        record: SketchPersistentSlotRecord,
        referencePlane: ReferencePlane,
        resolvePoolPoint: (pointId: string) => SketchPointObject,
        startCapArc: SketchArcObject,
        endCapArc: SketchArcObject,
    ): SketchArcObject {
        // 几何元数据是严格 JSON 透传载荷（SketchPersistentPlainData），
        // 必须先收窄为对象形态才能按字段读取；旧文档该载荷缺失或形态不符时直接判定数据不足。
        const data = record.geometryData;
        if (data === null || typeof data !== 'object' || Array.isArray(data)) {
            throw new Error('草图持久化重建失败：旧版圆弧槽口几何数据不足以合成构造中心线弧。');
        }
        // readonly 数组联合无法被 Array.isArray 守卫完全排除，校验通过后手动收窄为对象索引类型再读字段。
        const dataObject = data as {readonly [key: string]: SketchPersistentPlainData};
        const centerCandidate = dataObject.center;
        const center = Array.isArray(centerCandidate)
            && centerCandidate.length >= 3
            && centerCandidate.every((value) => typeof value === 'number' && Number.isFinite(value))
            ? (centerCandidate as unknown as SketchPersistentVector3)
            : undefined;
        const radius = Number(dataObject.radius);
        const startAngle = Number(dataObject.startAngle);
        const endAngle = Number(dataObject.endAngle);
        const clockwise = Boolean(dataObject.clockwise);
        if (
            !center
            || !Number.isFinite(radius)
            || radius <= 0
            || !Number.isFinite(startAngle)
            || !Number.isFinite(endAngle)
        ) {
            throw new Error('草图持久化重建失败：旧版圆弧槽口几何数据不足以合成构造中心线弧。');
        }

        const arc = new SketchArcObject({
            // 严格复用共享点：端帽弧圆心 = 中心线弧端点，槽口弧圆心 = 中心线弧圆心。
            startPoint: startCapArc.getCenterPoint(),
            endPoint: endCapArc.getCenterPoint(),
            centerPoint: resolvePoolPoint(record.arcCenterPointId!),
            definition: {
                centerWorld: toVector3(center),
                radius,
                startAngle,
                endAngle,
                clockwise,
            },
            referencePlane,
            isConstruction: true,
        });
        // 合成实体是运行时新成员，必须分配独立持久身份。
        arc.persistentId = createSketchEntityPersistentId();
        return arc;
    }

    /**
     * 重建集合容器。
     * @param record 集合记录。
     * @param referencePlane 参考平面。
     * @param resolvePoolPoint 点池解析函数。
     * @param resolveEdge 实体解析函数。
     * @returns 重建后的集合对象。
     */
    private rebuildCollection(
        record: SketchPersistentCollectionRecord,
        referencePlane: ReferencePlane,
        resolvePoolPoint: (pointId: string) => SketchPointObject,
        resolveEdge: (entityId: string) => ISketchObject,
        persistentIdToUuid: ReadonlyMap<string, string>,
    ): SketchCollectionObject {
        const sources = record.sourceEntityIds.map((entityId) => resolveEdge(entityId) as CollectSource);
        const collection = new SketchCollectionObject({
            referencePlane,
            sources,
            creationSource: record.creationSource as SketchCollectionCreationSource,
        });
        collection.persistentId = record.entityId;

        // 直接子对象补齐：多边形辅助圆、矩形构造对角线与中心草图点。
        record.directChildEntityIds.forEach((entityId) => {
            const child = resolveEdge(entityId) as unknown as THREE.Object3D;
            if (child.parent !== collection) {
                collection.add(child);
            }
        });
        record.directChildPointIds.forEach((pointId) => {
            const point = resolvePoolPoint(pointId);
            if (point.parent !== collection) {
                collection.add(point);
            }
        });

        if (record.geometryData !== null) {
            collection.geometryData = remapSketchPersistentPlainDataIds(
                record.geometryData,
                persistentIdToUuid,
            ) as Record<string, unknown>;
        }
        if (record.hasContour) {
            collection.syncContour();
        }
        return collection;
    }

    /**
     * 重建几何关系。
     * @param document 持久化文档。
     * @param group 目标草图组。
     */
    private rebuildRelations(document: SketchPersistentDocument, group: SketchGroup): void {
        document.relations.forEach((record) => {
            const targets = record.targetIds.map((targetId) => {
                const target = group.getConstraintTargetByPersistentId(targetId);
                if (!target) {
                    throw new Error(`草图持久化重建失败：关系 ${record.constraintId} 的目标 ${targetId} 不在组内。`);
                }
                return target as unknown as ISketchObject;
            });

            if (record.relationKind === SketchRelationKind.Symmetric) {
                const [axisTarget, sourceTarget, mirroredTarget] = targets;
                if (
                    targets.length !== 3
                    || new Set(targets).size !== 3
                    || !isMirrorAxisGeometryProvider(axisTarget)
                    || !sourceTarget
                    || !mirroredTarget
                ) {
                    throw new Error(
                        `草图持久化重建失败：关系 ${record.constraintId} 的 Symmetric 目标类型不合法，`
                        + '必须按“镜像轴、源草图实体、镜像草图实体”排序。',
                    );
                }
            }

            const constraint = this.instantiateConstraint(record, targets);
            constraint.persistentId = record.constraintId;
            constraint.setSuppressed(record.suppressed === true);
            constraint.setBranchHint(record.branchHint ?? null);
            targets.forEach((target) => {
                target.constraints.addRelation(
                    constraint as Parameters<typeof target.constraints.addRelation>[0],
                );
            });
        });
    }

    /**
     * 按“关系类型 + 首目标类型”实例化约束。
     * @param record 关系记录。
     * @param targets 关系的有序目标对象。
     * @returns 新建约束实例。
     */
    private instantiateConstraint(
        record: SketchPersistentRelationRecord,
        targets: readonly ISketchObject[],
    ): SketchRelationDefinition<ISketchObject> {
        const relationKind = record.relationKind as SketchRelationKind;
        if (relationKind === SketchRelationKind.Patterned) {
            return createSketchPatternedConstraintFromFormulaPayload(record.formulaPayload);
        }
        const firstTarget = targets[0]!;
        if (relationKind === SketchRelationKind.Symmetric) {
            const [axisTarget, sourceTarget, mirroredTarget] = targets;
            try {
                return new SketchEntitySymmetricConstraint(
                    axisTarget,
                    sourceTarget,
                    mirroredTarget,
                );
            } catch (error) {
                throw new Error(
                    `草图持久化重建失败：关系 ${record.constraintId} 的 Symmetric 目标协议非法：`
                    + `${error instanceof Error ? error.message : String(error)}`,
                );
            }
        }
        let ConstraintClass: (new (value?: number) => SketchRelationDefinition<ISketchObject>) | undefined;

        if (isSketchPointObject(firstTarget)) {
            ConstraintClass = resolvePointRelationConstraintClassMap()[relationKind];
        } else if (isSketchArcObject(firstTarget)) {
            // 圆弧优先专属映射（如 Radius），回退圆系映射（如 Tangent/Concentric）。
            ConstraintClass = resolveArcRelationConstraintClassMap()[relationKind]
                ?? resolveCircleRelationConstraintClassMap()[relationKind];
        } else if (isSketchCircleObject(firstTarget) || isSketchEllipseObject(firstTarget)) {
            ConstraintClass = resolveCircleRelationConstraintClassMap()[relationKind];
        } else if (
            isSketchLineObject(firstTarget)
            || isSketchParabolaObject(firstTarget)
            || isSketchSplineObject(firstTarget)
            || isSketchStyleSplineObject(firstTarget)
        ) {
            ConstraintClass = resolveLineRelationConstraintClassMap()[relationKind];
        }

        if (!ConstraintClass) {
            throw new Error(
                `草图持久化重建失败：关系 ${record.constraintId} 的类型 ${record.relationKind} 无法解析约束类`
                + `（首目标 objectType=${String(Reflect.get(firstTarget, 'objectType'))}）。`,
            );
        }

        /**
         * 数值关系经构造函数恢复锁定值；其余关系走无参构造。
         * 注意与序列化侧 `resolveNumericValue` 保持同一份清单：任一侧漏登记，
         * 数值都会在重开文档后丢失、关系退化为"按当前几何回填"。
         */
        if (record.numericValue !== null && isNumericSketchRelationKind(relationKind)) {
            return new ConstraintClass(record.numericValue);
        }
        return new ConstraintClass();
    }
}
